import { regReactPlugin } from "../react-plugin";
import {
  FunctionComponentTag, ClassComponentTag, HostComponentTag, HostTextTag, ForwardRefTag, Fiber,
} from '../react-plugin/reactTools';

export interface ReactRenderCallback {
  (renderInfo: ReactRenderData): void;
}

export interface ReactRenderOption {
  callback?: ReactRenderCallback;
}

// 最大渲染耗时(ms)
const MAX_ACTUAL_DURATION = 100;

// 最大dom树渲染耗时(ms)
const MAX_TREE_BASE_DURATION = 100;

const searchPatt = /wea_link_keep_react_render/i;

const REACT_DEVTOOLS_GLOBAL_HOOKS_KEY = '__REACT_DEVTOOLS_GLOBAL_HOOK__';

// 是否存在全局插件开启耗时计算
function hasGlobalHooks () {
  return window[REACT_DEVTOOLS_GLOBAL_HOOKS_KEY];
}

// 是否通过地址栏参数开启耗时计算
function hasLinkEnabled () {
  const search = window.location?.search || '';
  if (search && searchPatt.test(search)) {
    return true;
  }
  return false;
}

// 判断当前是否开启了耗时计算
// function isProfiling () {
//   return hasGlobalHooks() || hasLinkEnabled();
// }

// 监控开启全局 DevTools 工具标识
// const ENABLE_MONITOR_REACT_RENDER_SIGN = '__ENABLE_MONITOR_REACT_RENDER_SIGN__';

regReactPlugin('shouldEnableProfilerTimer', (options) => {
  const { enableProfilerTimer } = options || {};
  if (enableProfilerTimer) return options;
  function setEnable (sign: boolean) {
    if (typeof options === 'object') {
      options.enableProfilerTimer = sign;
      return options;
    } else {
      return { enableProfilerTimer: sign };
    }
  }
  if (hasLinkEnabled()) {
    return setEnable(true);
  }
  return options;
});

export interface ReactRenderData {
  /* node 节点的 weId */
  weId: string;
  /* 显示名 */
  displayName?: string;
  /* 节点耗时 */
  actualDuration?: number;
  /* dom树耗时 */
  treeBaseDuration?: number;
  /* 节点类型 */
  nodeType?: string;
  /* 节点的id */
  nodeId?: string;
  /* 节点的类名 */
  nodeClassName?: string;
}

class ReactRender {

  enable = false;

  weIdMap: { [key: string]: boolean } = {};

  callback?: ReactRenderCallback;

  constructor(options: ReactRenderOption) {
    const { callback } = options;
    this.enable = hasGlobalHooks() ? false : hasLinkEnabled() ? true : false;
    this.callback = callback;
    this.initListener();
  }

  initListener() {
    regReactPlugin('actualDurationChange', this.hanleDurationChange);
    regReactPlugin('treeBaseDurationChange', this.hanleDurationChange);
  }

  reset() {
    this.disabledProfiling();
    this.weIdMap = {};
  }

  // 排除特殊的 fiber 节点
  filterSpecialFiber (fiberNode: any) {
    if (!fiberNode.type) return true;
    return (
      [FunctionComponentTag, ClassComponentTag, HostComponentTag, HostTextTag, ForwardRefTag].includes(fiberNode.tag) &&
      (
        (fiberNode.type.displayName && fiberNode.type.displayName?.indexOf('withRouter(') !== -1) ||  // withRouter
        fiberNode.type.displayName === 'Router' || // Router
        fiberNode.type.displayName === 'MobXProvider' // MobxProvider
      )
    );
  }

  // 获取节点耗时
  hanleDurationChange = (option: any) => {
    if (!this.enable) return option;
    const { node } = option;
    if (!(node?.memoizedProps?.weId && (node?.actualDuration || node?.treeBaseDuration))) return option;
    const fiber = node as Fiber;
    // 过滤掉其他fiber节点
    if (this.filterSpecialFiber(fiber)) return;
    const weId = fiber.memoizedProps?.weId;
    // 已经上报过的weId，无需重复上报
    if (weId in this.weIdMap) return option;
    const { actualDuration, treeBaseDuration } = fiber;
    // 如果耗时超过100ms，则上报
    if ((actualDuration !== undefined && actualDuration >= MAX_ACTUAL_DURATION) ||
      (treeBaseDuration !== undefined && treeBaseDuration >= MAX_TREE_BASE_DURATION)) {
      const data: ReactRenderData = {
        weId,
        actualDuration,
        treeBaseDuration,
        nodeClassName: fiber.memoizedProps?.className,
        nodeId: fiber.memoizedProps?.id,
      };
      if (fiber.type?.displayName) {
        data.displayName = fiber.type?.displayName;
      }

      // switch(fiber.tag) {
      //   case FunctionComponentTag: // 函数组件
      //   case ClassComponentTag: // 类组件
      //     try {
      //       data.nodeType = fiber.type?.toString?.();
      //     } catch (e) {}
      //     break;
      //   case HostComponentTag: // 原生 dom 组件
      //   case HostTextTag:
      //     try {
      //       data.nodeType = fiber.type?.toString?.();
      //     } catch (e) {}
      //     break;
      //   case ForwardRefTag: // ForwardRef 组件
      //     try {
      //       data.nodeType = fiber.type?.render?.toString?.();
      //     } catch (e) {}
      //     break;
      // }
      // 上报数据
      this.callback?.(data);
      // 记录 weId
      this.weIdMap[weId] = true;
    };
    return option;
  }

  enableProfiling () {
    this.enable = true;
  }

  disabledProfiling () {
    this.enable = false;
  }

  startProfiling () {
    if (hasGlobalHooks()) {
      this.enableProfiling();
      // 如果存在全局插件无需单独处理
      return ;
    }
    if (!hasLinkEnabled()) {
      // 往地址栏上添加参数，刷新页面
      const url = window.location.href;
      if (url.indexOf('?') !== -1) {
        window.location.href = `${url}&wea_link_keep_react_render=1`;
      } else {
        window.location.href = `${url}?wea_link_keep_react_render=1`;
      }
    }
  }

  stopProfiling () {
    if (hasGlobalHooks()) {
      this.reset();
      // 如果存在全局插件无需单独处理
      return;
    }
    // const url = window.location.href;
    // // 去除地址栏上的参数，刷新页面
    // if (hasLinkEnabled()) {
    //   let newUrl = url;
    //   if (newUrl.indexOf('&wea_link_keep_react_render=1') !== -1) {
    //     newUrl = newUrl.replace('&wea_link_keep_react_render=1', '');
    //   }
    //   if (newUrl.indexOf('?wea_link_keep_react_render=1&') !== -1) {
    //     newUrl = newUrl.replace('?wea_link_keep_react_render=1&', '?');
    //   }
    //   if (newUrl.indexOf('?wea_link_keep_react_render=1') !== -1) {
    //     newUrl = newUrl.replace('?wea_link_keep_react_render=1', '');
    //   }
    //   if (url !== newUrl){
    //     this.setHref(newUrl);
    //   }
    // }
  }

  setHref = (url: string) => {
    // TODO: 弹出提示框
    window.location.href = url;
  }
}

export default ReactRender;
